import React, { forwardRef } from "react";

import {
  SearchField as AriaTextField,
  TextFieldProps as AriaTextFieldProps,
  ValidationResult,
} from "react-aria-components";

import { Button } from "./Button";
import { Description, FieldError, FieldGroup, Input, Label } from "./Field";
import { DenovoIcon } from "./icons";
import { composeTailwindRenderProps } from "./utils";

export interface IExtendedTextFieldProps extends AriaTextFieldProps {
  ref: React.RefObject<HTMLInputElement>;
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  icon?: React.ReactNode;
  appendIcon?: React.ReactNode;
  onClear?: () => void;
  clearable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  descriptionClassName?: string;
  inputClassName?: string;
  onSubmit?: (value: string) => void;
  fieldSize?: "small" | "medium" | "large";
}

export const TextField = forwardRef<HTMLInputElement, IExtendedTextFieldProps>(
  (
    {
      label,
      description,
      errorMessage,
      icon,
      appendIcon,
      clearable = false,
      placeholder,
      inputClassName,
      descriptionClassName,
      fieldSize,
      ...props
    },
    ref,
  ) => {
    const hasError = !!errorMessage;

    return (
      <AriaTextField
        {...(props as any)}
        isInvalid={hasError}
        className={composeTailwindRenderProps(
          props.className as string,
          "group flex flex-col gap-2 min-w-0",
        )}>
        {label && (
          <Label
            isDisabled={props.isDisabled}
            isInvalid={hasError}
            className="text-pl font-medium text-contentPrimary data-[disabled]:text-contentDisabled">
            {label}
            {props.isRequired && (
              <span className="text-contentAlert ms-1">*</span>
            )}
          </Label>
        )}
        {description && (
          <Description
            className={`text-pm text-contentTertiary ${descriptionClassName || ""}`}>
            {description}
          </Description>
        )}
        <FieldGroup
          isInvalid={hasError}
          size={fieldSize}
          className="relative rounded-sm px-0 overflow-visible transition-colors duration-base ease-out">
          {icon && (
            <div className="absolute start-3 top-1/2 transform -translate-y-1/2 text-contentSecondary pointer-events-none">
              {icon}
            </div>
          )}
          <Input
            ref={ref}
            placeholder={placeholder}
            className={[
              "w-full h-full text-pl bg-transparent text-contentPrimary outline-none transition-colors duration-base ease-out disabled:text-contentTertiary disabled:opacity-60 disabled:cursor-not-allowed",
              "[&::-webkit-search-cancel-button]:appearance-none",
              hasError
                ? "placeholder:text-contentAlert"
                : "placeholder:text-contentTertiary",
              icon ? "ps-10" : "ps-3",
              appendIcon || clearable ? "pe-10" : "pe-3",
              inputClassName || "",
            ]
              .filter(Boolean)
              .join(" ")}
            isInvalid={hasError}
          />
          {clearable && (
            <Button
              variant="transparent"
              size="small"
              onPress={() => props?.onClear && props?.onClear()}
              className="absolute end-2 top-1/2 transform -translate-y-1/2 p-1 text-contentSecondary hover:text-contentPrimary">
              <DenovoIcon
                icon="close"
                className="w-4 h-4"
                color="currentColor"
              />
            </Button>
          )}
          {appendIcon && !clearable && (
            <div className="absolute end-3 top-1/2 transform -translate-y-1/2 text-contentSecondary pointer-events-none">
              {appendIcon}
            </div>
          )}
        </FieldGroup>

        <FieldError className="text-pm text-contentAlert">
          {errorMessage}
        </FieldError>
      </AriaTextField>
    );
  },
);
